import { DataKeys } from "../../types";
import aimmaConceptImg from "../../image/health/aimma_ai_powered_healthcare_coordination.png";
import { useLang } from "../Context";
import { SlideInAnim } from "../tools/Animations";
import { HealthSection } from "./HealthSection";
import { HealthSectionHeader } from "./HealthSectionHeader";
import { getLocalized, HealthLang } from "./healthTypes";
import {
    HEALTH_BG_GREY,
    healthBody,
    healthCardTitle,
    healthSectionEyebrow,
    healthSolutionAimmaAccent,
    healthSolutionCapabilityAccent,
    healthSolutionCapabilityIconWrap,
    healthSolutionCapabilityItemCard,
    healthSolutionCapabilitiesGrid,
    healthSolutionCapabilitiesHeading,
    healthSolutionDisclaimerIcon,
    healthSolutionDisclaimerRow,
    healthSolutionImageFrame,
    healthSolutionImageSurface,
    healthSolutionMultiAgentCallout,
    healthSolutionMultiAgentTitle,
} from "./healthVisualStyles";

interface HealthSolutionSectionProps {
    data: DataKeys;
}

const capabilityIcons = [
    <svg key="shared" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="6" cy="6" r="2.2" />
        <circle cx="18" cy="7" r="2.2" />
        <circle cx="12" cy="18" r="2.2" />
        <path d="M8 7.2 15.4 8.1" />
        <path d="M7.2 7.6 10.6 15.8" />
        <path d="M16.2 9.1 13 16" />
    </svg>,
    <svg key="role" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.8 18.2c1.8-2.4 4-3.6 6.2-3.6 2.2 0 4.4 1.2 6.2 3.6" />
    </svg>,
    <svg key="coordination" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="6" cy="8" r="2.1" />
        <circle cx="18" cy="8" r="2.1" />
        <circle cx="12" cy="16.5" r="2.1" />
        <path d="M8.2 8h7.6" />
        <path d="M7.4 9.6l3 4.7" />
        <path d="M16.6 9.6l-3 4.7" />
    </svg>,
    <svg key="human" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3.5 19 6v5.5c0 3.6-2.4 6.6-7 9-4.6-2.4-7-5.4-7-9V6z" />
        <path d="m9 11.8 2.1 2.1L15 10" />
    </svg>,
] as const;

function splitCalloutLabel(text: string): { label: string; rest: string } {
    const colonMatch = text.match(/^([^:：]+)[:：]\s*(.+)$/s);
    if (colonMatch) {
        return { label: colonMatch[1].trim(), rest: colonMatch[2].trim() };
    }

    /** Japanese callout uses 「。」 after the heading phrase (e.g. なぜMulti-Agentなのか。) */
    const jpPeriodMatch = text.match(/^(.+?)。\s*(.+)$/s);
    if (jpPeriodMatch) {
        return { label: jpPeriodMatch[1].trim(), rest: jpPeriodMatch[2].trim() };
    }

    return { label: "", rest: text };
}

export function HealthSolutionSection({ data }: HealthSolutionSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const title = getLocalized(data?.title, hLang);
    const aimmaDefinition = getLocalized(data?.aimmaDefinition, hLang);
    const body = getLocalized(data?.body, hLang);
    const pillars: DataKeys[] = data?.pillars ?? [];
    const disclaimer = getLocalized(data?.disclaimer, hLang);
    const callout = body ? splitCalloutLabel(body) : { label: "", rest: "" };
    const sectionId = "solution";

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact
            style={{ backgroundColor: HEALTH_BG_GREY }}
        >
            <div className="px-1 py-1 sm:px-2">
                <div className="grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.52fr)_minmax(0,0.48fr)] lg:items-center lg:gap-10">
                    <div className="space-y-4 lg:pr-2">
                        <HealthSectionHeader
                            title={title}
                            titleId={`${sectionId}-title`}
                            className="mb-0 lg:mb-0"
                        />
                        <p className={healthSectionEyebrow}>
                            Aimma — AI Medical Multi-Agent
                        </p>
                        <span className={healthSolutionAimmaAccent} aria-hidden="true" />
                        {aimmaDefinition && (
                            <p className={`${healthBody} mt-4 max-w-xl text-base leading-relaxed text-grey-600 sm:text-[1.05rem] sm:leading-7`}>
                                {aimmaDefinition}
                            </p>
                        )}
                        <div className={healthSolutionMultiAgentCallout}>
                            <div className="min-w-0">
                                {callout.label ? (
                                    <p className={healthSolutionMultiAgentTitle}>{callout.label}</p>
                                ) : null}
                                <p className={`${healthBody} ${callout.label ? "mt-1" : ""} text-sm leading-relaxed text-grey-600`}>
                                    {callout.rest || body}
                                </p>
                            </div>
                        </div>
                    </div>
                    <SlideInAnim direction="right">
                        <div className={healthSolutionImageFrame}>
                            <img
                                src={aimmaConceptImg}
                                alt="Aimma AI-powered healthcare coordination"
                                className={healthSolutionImageSurface}
                                loading="lazy"
                            />
                        </div>
                    </SlideInAnim>
                </div>

                <div className="mt-8 w-full min-w-0 lg:mt-10">
                    <p className={healthSolutionCapabilitiesHeading}>CORE CAPABILITIES</p>
                    <div className={healthSolutionCapabilitiesGrid}>
                        {pillars.map((pillar, index) => (
                            <article key={pillar.id ?? index} className={`${healthSolutionCapabilityItemCard} min-h-[220px]`}>
                                <div className="mb-3 flex w-full items-start justify-between gap-3">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-teal-500">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <span className={healthSolutionCapabilityIconWrap} aria-hidden="true">
                                        {capabilityIcons[index]}
                                    </span>
                                </div>
                                <h3 className={`${healthCardTitle} text-base font-semibold text-grey-900 sm:text-lg`}>
                                    {getLocalized(pillar.title, hLang)}
                                </h3>
                                <span className={healthSolutionCapabilityAccent} aria-hidden="true" />
                                <p className={`${healthBody} mt-2 text-sm leading-relaxed text-grey-600`}>
                                    {getLocalized(pillar.text, hLang)}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>

                {disclaimer && (
                    <p className={healthSolutionDisclaimerRow}>
                        <span className={healthSolutionDisclaimerIcon} aria-hidden="true">
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3.5 19 6v5.5c0 3.6-2.4 6.6-7 9-4.6-2.4-7-5.4-7-9V6z" />
                            </svg>
                        </span>
                        <span className="text-left [text-align:left]">
                            {disclaimer}
                        </span>
                    </p>
                )}
            </div>
        </HealthSection>
    );
}
